import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { App, MapFieldPoint, Project } from '../../models';
import { FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS } from '../../shared/constants';
import { FieldType, FieldTypePrimitive, ReportFilterType } from '../../shared/enums';
import { MapDataService } from '../data-services';
import { FieldTypes, MapFieldPointDefaultFieldTypes } from './enums/field-types';
import { Column, Field, FieldProperty, Options, Section } from './models';

@Injectable({
    providedIn: 'root'
})
export class FieldsLogicService {

    listOfFields: Field[];
    gridFieldsToFields: Field[];
    listOfFieldsAndReadOnlyFields: Field[];
    listOfMapFieldPointFields: Field[];

    constructor(private _mapDataService: MapDataService) { }

    getAllFields(currentApp: App, project: Project): Observable<Field[]> {
        return this.commonLogic(currentApp, project).pipe(
            map(() => this.listOfFields)
        );
    }

    getAllFieldsAndReadOnlyFields(currentApp: App, project: Project): Observable<Field[]> {
        return this.commonLogic(currentApp, project).pipe(
            map(() => this.listOfFieldsAndReadOnlyFields)
        );
    }

    getGridFieldsToFields(currentApp: App, project: Project): Observable<Field[]> {
        return this.commonLogic(currentApp, project).pipe(
            map(() => this.gridFieldsToFields)
        );
    }

    commonLogic(currentApp: App, project: Project): Observable<void> {
        this.listOfFields = new Array<Field>();
        this.listOfFieldsAndReadOnlyFields = new Array<Field>();
        this.gridFieldsToFields = new Array<Field>();

        project.JSONForms?.Sections?.forEach((section: Section) => {
            this.sectionObject(section);
        });

        if (project.isSummaryProject) {
            this.loadFieldsData();
            return of(void 0);
        }

        return this.getMapFieldPointByProject(currentApp, project).pipe(tap(() => {
            this.loadFieldsData();
        }), map(() => void 0));

    }

    getMapFieldPointByProject(currentApp: App, project: Project): Observable<void> {
        return this._mapDataService.getMapFieldPointByProject(currentApp, project).pipe(tap((mapFieldPoint) => {
            this.loadMapFieldPointFields(mapFieldPoint);

        }), map(() => void 0));
    }

    loadMapFieldPointFields(fieldPoint: MapFieldPoint): void {
        if (!fieldPoint?.columns || fieldPoint.columns.length === 0) {
            return;
        }

        this.listOfMapFieldPointFields = [];

        fieldPoint.columns.forEach(column => {
            const newField = new Field();

            newField.id = column.id;
            newField.dataId = fieldPoint.id;
            newField.Name = column.header;
            newField.SelectOption = this.getFieldTypeIdAndSelectOption(column);
            newField.FieldType = new FieldProperty();
            newField.FieldType.id = this.getFieldTypeIdAndSelectOption(column);
            newField.FieldType.options = this.getOptions(column);
            newField.FieldType.text = this.getTextType(column);
            newField.FieldType.required = false;
            newField.FieldType.type = this.getFieldTypePrimitive(column);
            newField.FieldType.upload = this.getFieldTypeUpload(column);
            newField.FieldType.enum = this.getFieldTypeEnumValues(column);
            newField.FieldType.enableSubRow = fieldPoint.enableSubRow;
            newField.breadcrumb = `${FieldTypes.FieldPoints} > ${column.header} (grid) >`;
            newField.parent = `... > ${FieldTypes.FieldPoints}`;

            this.listOfMapFieldPointFields.push(newField);
        });

        this.listOfFields.push(...this.listOfMapFieldPointFields);
        this.gridFieldsToFields.push(...this.listOfMapFieldPointFields);
    }

    loadFieldsData(): void {
        // Summary projects can place the same source field in more than one section; field lists only need it once.
        this.listOfFields = this.getDistinctFields(this.listOfFields);
        this.gridFieldsToFields = this.getDistinctFields(this.gridFieldsToFields);

        this.listOfFields.sort((a, b) => (a.Name > b.Name) ? 1 : -1);
        this.listOfFieldsAndReadOnlyFields = this.listOfFields;
        this.listOfFields = this.listOfFields.filter((field: Field) => !field.FieldType?.options?.readOnly);

        this.gridFieldsToFields = this.gridFieldsToFields.filter((field: Field) => !field.FieldType?.options?.readOnly);
        this.gridFieldsToFields.sort((a, b) => (a.Name > b.Name) ? 1 : -1);
    }

    sectionsArray(sections: Section[]): void {
        sections.forEach((section: Section) => {
            if (section.Columns?.length) {
                this.columnsArray(section.Columns);
            }

            if (section.Sections?.length) {
                this.sectionsArray(section.Sections);
            }
        });
    }

    sectionObject(section: Section): void {
        if (section.Columns?.length) {
            this.columnsArray(section.Columns);
        }

        if (section.Sections?.length) {
            this.sectionsArray(section.Sections);
        }
    }

    columnsArray(columns: Column[]): void {
        columns.forEach((column: Column) => {
            this.columnObject(column);
        });
    }

    columnObject(column: Column): void {
        if (column.Fields?.length) {
            this.fieldsArray(column.Fields);
        }
    }

    fieldsArray(fields: Field[]): void {
        fields.forEach((field: Field) => {
            if (field.SelectOption === FieldType.Grid) {
                this.convertGridColumnsToFields(field);
            }
            else {
                this.gridFieldsToFields.push(field);
            }

            this.listOfFields.push(field);
        });
    }

    setDefaultReportFilterType(field: Field): void {
        if (!field.reportFilterType) {
            field.reportFilterType = ReportFilterType.StringSearch;
        }
    }

    convertGridColumnsToFields(field: Field): void {
        field.FieldType.options.columns.forEach((column: Column) => {
            const newField = new Field();

            newField.dataId = field.id;
            newField.id = column.id;
            newField.Name = column.header;
            newField.SelectOption = this.getFieldTypeIdAndSelectOption(column);
            newField.FieldType = new FieldProperty();
            newField.FieldType.id = this.getFieldTypeIdAndSelectOption(column);
            newField.FieldType.options = this.getOptions(column);
            newField.FieldType.text = this.getTextType(column);
            newField.FieldType.required = field.FieldType.required;
            newField.FieldType.type = this.getFieldTypePrimitive(column);
            newField.FieldType.upload = this.getFieldTypeUpload(column);
            newField.FieldType.enum = this.getFieldTypeEnumValues(column);
            newField.FieldType.enableSubRow = field.FieldType.enableSubRow;
            newField.breadcrumb = `${field.breadcrumb} ${field.Name} (grid) >`;
            newField.parent = `... > ${field.Name}`;
            newField.projectId = field.projectId;

            this.gridFieldsToFields.push(newField);
        });
    }

    getFieldTypeIdAndSelectOption(column: Column): FieldType | undefined {
        if (column.type === "Text") {
            return FieldType.TextBox;
        }

        if (column.type === FieldTypes.TextArea) {
            return FieldType.TextArea;
        }

        if (column.type === FieldTypes.DateSelect) {
            return FieldType.DateSelect;
        }

        if (column.type === FieldTypes.Number) {
            return FieldType.Number;
        }

        if (column.type === FieldTypes.FileUpload) {
            return FieldType.Uploader;
        }

        if (column.type === FieldTypes.TrueFalse) {
            return FieldType.CheckBox;
        }

        if (column.type === FieldTypes.DropDown) {
            return FieldType.DropDown;
        }

        if (column.type === FieldTypes.EmailHyperlink) {
            return FieldType.EmailHyperlink;
        }

        if (column.type === FieldTypes.URLHyperlink) {
            return FieldType.URLHyperlink;
        }

        if (column.type === FieldTypes.Person) {
            return FieldType.Person;
        }

        if (column.type === MapFieldPointDefaultFieldTypes.PointCategory) {
            return FieldType.PointCategory;
        }

        if (column.type === MapFieldPointDefaultFieldTypes.Coordinates) {
            return FieldType.Coordinates;
        }

        if (column.type === FieldTypes.Formula) {
            return FieldType.Formula;
        }
    }

    getOptions(column: Column): Options | undefined {
        if (column.type === FieldTypes.TextArea) {
            const options = new Options();
            options.multi = true;
            return options;
        }

        if (column.type === FieldTypes.DateSelect) {
            const options = new Options();
            options.dateFormat = FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE_FORMAT;
            options.dateSaveFormat = FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE_SAVE_FORMAT;
            options.format = FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE;
            return options;
        }

        if (column.type === FieldTypes.FileUpload) {
            const options = new Options();
            options.allowedExtensions = [];
            options.maxFileSize = 100000000;
            return options;
        }

        if (column.type === FieldTypes.TrueFalse) {
            const options = new Options();
            options.toggle = true;
            return options;
        }

        if (column.type === FieldTypes.Number) {
            const options = new Options();
            options.format = column.format;
            options.decimalPlaces = column.decimalPlaces;
            return options;
        }
    }

    getTextType(column: Column): string | undefined {
        if (column.type === "Text") {
            return FieldTypes.TextBox;
        }

        if (column.type === FieldTypes.TextArea) {
            return FieldTypes.TextArea;
        }

        if (column.type === FieldTypes.DateSelect) {
            return FieldTypes.DateSelect;
        }

        if (column.type === FieldTypes.Number) {
            return FieldTypes.Number;
        }

        if (column.type === FieldTypes.FileUpload) {
            return FieldTypes.Uploader;
        }

        if (column.type === FieldTypes.TrueFalse) {
            return FieldTypes.CheckBox;
        }

        if (column.type === FieldTypes.DropDown) {
            return FieldTypes.DropDown;
        }

        if (column.type === FieldTypes.EmailHyperlink) {
            return FieldTypes.EmailHyperlink;
        }

        if (column.type === FieldTypes.URLHyperlink) {
            return FieldTypes.URLHyperlink;
        }

        if (column.type === FieldTypes.Person) {
            return FieldTypes.Person;
        }

        if (column.type === MapFieldPointDefaultFieldTypes.PointCategory) {
            return MapFieldPointDefaultFieldTypes.PointCategory;
        }

        if (column.type === MapFieldPointDefaultFieldTypes.Coordinates) {
            return MapFieldPointDefaultFieldTypes.Coordinates;
        }

        if (column.type === FieldTypes.Formula) {
            return FieldTypes.Formula;
        }
    }

    getFieldTypePrimitive(column: Column): string | undefined {
        if (column.type === "Text") {
            return FieldTypePrimitive.String;
        }

        if (column.type === FieldTypes.TextArea) {
            return FieldTypePrimitive.String;
        }

        if (column.type === FieldTypes.DateSelect) {
            return FieldTypePrimitive.String;
        }

        if (column.type === FieldTypes.Number) {
            return FieldTypePrimitive.Number;
        }

        if (column.type === FieldTypes.TrueFalse) {
            return FieldTypePrimitive.Boolean;
        }

        if (column.type === FieldTypes.DropDown) {
            return FieldTypePrimitive.String;
        }

        if (column.type === FieldTypes.EmailHyperlink) {
            return FieldTypePrimitive.String;
        }

        if (column.type === FieldTypes.URLHyperlink) {
            return FieldTypePrimitive.String;
        }

        if (column.type === MapFieldPointDefaultFieldTypes.Coordinates) {
            return MapFieldPointDefaultFieldTypes.Coordinates;
        }

        if (column.type === MapFieldPointDefaultFieldTypes.PointCategory) {
            return MapFieldPointDefaultFieldTypes.PointCategory;
        }

        if (column.type === FieldTypes.Formula) {
            return column.format == FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE ? FieldTypePrimitive.String : FieldTypePrimitive.Number;
        }
    }

    getFieldTypeUpload(column: Column): boolean | undefined {
        if (column.type === FieldTypes.FileUpload) {
            return true;
        }
    }

    getFieldTypeEnumValues(column: Column): string[] | undefined {
        if (column.type === FieldTypes.DropDown) {
            return column.enum;
        }
    }

    private getDistinctFields(fields: Field[]): Field[] {
        const distinctFields: Field[] = [];
        const seenFieldKeys = new Set<string>();

        for (const field of fields) {
            const fieldKey = this.getFieldKey(field);

            if (seenFieldKeys.has(fieldKey)) {
                continue;
            }

            seenFieldKeys.add(fieldKey);
            distinctFields.push(field);
        }

        return distinctFields;
    }

    // Grid columns and field points share column ids across parents, so the parent's dataId is part of the key.
    private getFieldKey(field: Field): string {
        return `${field.dataId ?? ''}:${field.id}`;
    }
}
